import { useEffect, useState } from 'react';
import { Outlet, useLocation, useMatch } from 'react-router';
import * as RDialog from '@radix-ui/react-dialog';
import { AnimatePresence, motion } from 'motion/react';
import { Menu, PanelLeft, SquarePen } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { IconButton, Wordmark } from '@/components/ui';
import { Link } from 'react-router';
import { StreamStoreContext, useCreateStreamStore } from '@/features/chat/streamStore';
import { ConsentDialog } from '@/features/chat/ConsentDialog';
import { useChats } from '@/features/chat/api';
import { useMediaQuery } from '@/lib/media';
import { readStorage, writeStorage } from '@/lib/storage';
import { SidebarContent } from './Sidebar';

const SIDEBAR_W = 304;

export default function AppShell() {
  const qc = useQueryClient();
  const store = useCreateStreamStore(qc);
  const desktop = useMediaQuery('(min-width: 1024px)');
  const [collapsed, setCollapsed] = useState(() => readStorage('medicail.sidebar') === 'collapsed');
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const match = useMatch('/c/:chatId');
  const chats = useChats();
  const title = match ? chats.data?.find((c) => c.chatId === match.params.chatId)?.title : undefined;

  useEffect(() => setMobileOpen(false), [location.pathname]);

  const toggleDesktop = (next: boolean) => {
    setCollapsed(next);
    writeStorage('medicail.sidebar', next ? 'collapsed' : 'open');
  };

  return (
    <StreamStoreContext.Provider value={store}>
      <div className="flex h-dvh overflow-hidden bg-canvas">
        {desktop && (
          <motion.aside
            initial={false}
            animate={{ width: collapsed ? 0 : SIDEBAR_W }}
            transition={{ type: 'spring', bounce: 0, duration: 0.35 }}
            className="shrink-0 overflow-hidden border-line bg-sidebar"
            style={{ borderRightWidth: collapsed ? 0 : 1 }}
            inert={collapsed}
          >
            <div style={{ width: SIDEBAR_W }} className="h-full">
              <SidebarContent onCollapse={() => toggleDesktop(true)} />
            </div>
          </motion.aside>
        )}

        {!desktop && (
          <RDialog.Root open={mobileOpen} onOpenChange={setMobileOpen}>
            <AnimatePresence>
              {mobileOpen && (
                <RDialog.Portal forceMount>
                  <RDialog.Overlay asChild forceMount>
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-40 bg-overlay backdrop-blur-[2px]" />
                  </RDialog.Overlay>
                  <RDialog.Content asChild forceMount aria-describedby={undefined} onOpenAutoFocus={(e) => e.preventDefault()}>
                    <motion.div
                      initial={{ x: '-100%' }}
                      animate={{ x: 0 }}
                      exit={{ x: '-100%' }}
                      transition={{ type: 'spring', bounce: 0, duration: 0.35 }}
                      className="fixed inset-y-0 left-0 z-50 w-[min(88vw,20rem)] overflow-hidden border-r border-line bg-sidebar shadow-lift focus:outline-none"
                    >
                      <RDialog.Title className="sr-only">Navigation</RDialog.Title>
                      <SidebarContent onNavigate={() => setMobileOpen(false)} onCollapse={() => setMobileOpen(false)} />
                    </motion.div>
                  </RDialog.Content>
                </RDialog.Portal>
              )}
            </AnimatePresence>
          </RDialog.Root>
        )}

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-14 shrink-0 items-center gap-2 px-3 pt-[var(--safe-top)] sm:px-4" style={{ height: 'calc(3.5rem + var(--safe-top))' }}>
            {!desktop && (
              <IconButton label="Open menu" onClick={() => setMobileOpen(true)} tooltipSide="bottom">
                <Menu className="size-5" />
              </IconButton>
            )}
            {desktop && collapsed && (
              <>
                <IconButton label="Open sidebar" onClick={() => toggleDesktop(false)} tooltipSide="bottom" className="text-ink-muted">
                  <PanelLeft className="size-5" />
                </IconButton>
                <Link to="/" aria-label="MedicAiL home" className="rounded-xl">
                  <Wordmark markClassName="size-8" />
                </Link>
              </>
            )}
            <p className="min-w-0 flex-1 truncate text-center font-heading text-base font-semibold text-ink lg:pl-2 lg:text-left">{title}</p>
            {!desktop && (
              <Link to="/" aria-label="New chat" className="grid size-10 place-items-center rounded-xl text-ink transition-colors hover:bg-hover">
                <SquarePen className="size-5" />
              </Link>
            )}
          </header>
          <main className="min-h-0 flex-1">
            <Outlet />
          </main>
        </div>
      </div>
      <ConsentDialog />
    </StreamStoreContext.Provider>
  );
}
